import { useSelector } from 'react-redux';
import { PageState } from 'models/PageState';
import { getBrandUrl, getProductShortDescription } from 'views/ProductPage/selector';
import Markdown from 'components/Shared/Markdown';
import Image from 'components/Shared/Image';
import { isProductPage } from 'models/ProductPageModel';
import { getBrandWidget } from 'components/Shared/Product/selector';
import { BrandWidget } from 'models/ListViewProduct';
import styles from 'views/ProductPage/themes/BGH/sections/productShortDescription.scss';
import { DetailedProduct } from 'models/DetailedProduct';
import { ProductAttribute } from 'models/ProductAttribute';
import { hasElements } from 'utils';
import { ReactComponent } from 'models/ReactComponent';

const BRAND_LOGO_DIMENSIONS = {
  width: 112,
  height: 112
};

interface Props {
  brand?: BrandWidget;
  brandUrl: string;
  shortDescription: ReactComponent;
  summaryAttributes: ProductAttribute[];
}

export const _ProductShortDescription = ({
  brand,
  brandUrl,
  shortDescription,
  summaryAttributes
}: Props) => (
  <div className={styles.shortDescription}>
    {brand && (
      <a href={brandUrl} className={styles.brandLogoWrapper}>
        <Image className={styles.brandLogo} {...brand.image} {...BRAND_LOGO_DIMENSIONS} />
      </a>
    )}
    <Markdown className={styles.shortDescription} {...shortDescription} />
    {hasElements(summaryAttributes) && (
      <div className={styles.attributes}>
        {summaryAttributes.map(({ id, name, value }, i) => (
          <div className={styles.attribute} key={id}>
            <div className={styles.title}>{name}</div>
            <div>
              {i === 0 ? (
                <a href={brandUrl} className={styles.brandLink}>
                  {value}
                </a>
              ) : (
                value
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

interface ProductShortDescriptionProps {
  hideBrand?: boolean;
  hideAttributes?: boolean;
}

export const ProductShortDescription = ({
  hideBrand,
  hideAttributes
}: ProductShortDescriptionProps) => {
  const shortDescription = useSelector<PageState, ReactComponent>(state =>
    getProductShortDescription(state.currentPage)
  );
  const brand = useSelector<PageState, BrandWidget | undefined>(state =>
    isProductPage(state.currentPage) ? getBrandWidget(state.currentPage.product) : undefined
  );
  const brandUrl = useSelector<PageState, string>(state => getBrandUrl(state.currentPage));
  const summaryAttributes = useSelector<PageState, DetailedProduct['summaryAttributes']>(
    state => (isProductPage(state.currentPage) && state.currentPage.product.summaryAttributes) || []
  );

  return (
    <_ProductShortDescription
      brand={hideBrand ? undefined : brand}
      summaryAttributes={hideAttributes ? [] : summaryAttributes}
      {...{
        brandUrl,
        shortDescription
      }}
    />
  );
};
